import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import clsx from 'clsx';
import { Column } from '@nextiva/crm-datatable';

import { TableActionsPanel, withRouterQuery, AdminTable } from 'components';
import { getPermissionsViewType } from 'containers/pages/ManageUsers/UserApplications/duck/selectors';

import {
  getActionsGroupedByResource,
  getScopeFromSettings,
  getApplicationAccessPermission,
  getApplicationResources,
  RESOURCES_DEFAULT_QUERY,
} from '../duck/selectors';
import { permissionSelectRenderer, modelNameRenderer } from '../tableRenderers';

import styles from './ModelsTable.scss';

const defaultOption = {
  value: 0,
  label: 'Default',
};
const actionNames = ['create', 'view', 'edit', 'delete'];

const permissionsTitles = (names) =>
  names.map((name) => (
    <span key={name} className={styles.modelSelectorComponent}>
      {name.toUpperCase()}
    </span>
  ));

export const createDisplayPreferences = (
  showLabels,
  isOpen,
  options = {},
  className,
  productScope,
  disabled,
  actionsByResource,
) => [
  {
    name: 'name',
    label: 'RESOURCE',
    shouldRenderFilter: true,
    shouldRenderSort: true,
    display: true,
    width: '20%',
    type: 'text',
    typeConfig: {
      defaultOperator: 'contains',
      operatorOptions: [
        {
          value: 'contains',
          label: 'Resource Name contains',
        },
      ],
    },
    cellRenderer: modelNameRenderer({ isOpen }),
  },
  {
    name: 'permissions',
    label: permissionsTitles(actionNames),
    display: true,
    width: '70%',
    type: 'text',
    cellRenderer: permissionSelectRenderer({
      actionNames,
      options,
      className,
      showLabels,
      productScope,
      disabled,
      defaultOption,
      actionsByResource,
    }),
  },
];

class ModelsTable extends PureComponent {
  static defaultProps = {
    defaultQuery: RESOURCES_DEFAULT_QUERY,
  };

  handleRowClick = (e, rowData) => {
    const {
      location: { pathname },
      params: { modelName },
    } = this.props;

    if (!rowData.columns) return;

    const path = modelName
      ? pathname.replace(`/${modelName}`, '')
      : `${pathname}/${rowData.slug}`;

    this.props.router.push(path);
  };

  render() {
    const {
      resources,
      productScope,
      permissionsViewType,
      isLoading,
      actionsOptions,
      className,
      showLabels,
      isOpen,
      appAccessPermissions,
      getTableQuery,
      defaultQuery,
      filtersFromParams,
      handleFiltersChange,
      orderFromParams,
      handleSortChange,
      handlePageChange,
      actionsByResource,
      ...dataTableProps
    } = this.props;

    const { filters, sort, limit, page } = getTableQuery();
    const {
      sort: defaultSort,
      limit: defaultLimit,
      page: defaultPage,
    } = defaultQuery;
    const pageCount = Math.ceil(resources.totalCount / (limit || defaultLimit));
    const modelPermissionsDisabled = appAccessPermissions === false;

    const displayPreferences = createDisplayPreferences(
      showLabels,
      isOpen,
      actionsOptions,
      styles.modelSelectorComponent,
      productScope,
      modelPermissionsDisabled,
      actionsByResource,
    );

    const filterDecoders = displayPreferences.reduce(
      (res, { name, decodeFilter }) => ({ ...res, [name]: decodeFilter }),
      {},
    );

    const css = clsx(
      className,
      styles.dataTableContainer,
      permissionsViewType === 'list' && styles.hideHeader,
      !!resources.totalCount && styles['dataTableContainer--withPagination'],
    );

    return (
      <div className={css}>
        {resources.totalCount && (
          <TableActionsPanel
            pageCount={pageCount}
            forcePage={(page || defaultPage) - 1}
            defaultLimit={defaultLimit}
            onPageChange={!!pageCount && handlePageChange}
          />
        )}
        <AdminTable
          {...dataTableProps}
          isLoading={isLoading}
          data={resources}
          displayPreferences={displayPreferences}
          disableGear
          disableCheckboxes
          onRowClick={this.handleRowClick}
          onFiltersChange={(data) => handleFiltersChange(filterDecoders)(data)}
          onSortChange={handleSortChange}
        >
          {displayPreferences.map((item, index) => (
            <Column
              key={index}
              name={item.name}
              type={item.type}
              typeConfig={item.typeConfig}
              cellRenderer={item.cellRenderer}
              shouldRenderSort={get(item, 'shouldRenderSort', false)}
              shouldRenderFilter={get(item, 'shouldRenderFilter', false)}
              order={orderFromParams(sort || defaultSort, item.name)}
              filterData={filtersFromParams(
                filters,
                item.name,
                item.encodeFilter,
              )}
            >
              {item.label}
            </Column>
          ))}
        </AdminTable>
      </div>
    );
  }
}

// @TODO_PERMISSIONS Add propTypes

const mapStateToProps = (state, ownProps) => ({
  productScope: getScopeFromSettings(state),
  permissionsViewType: getPermissionsViewType(state),
  resources: getApplicationResources(state, ownProps),
  appAccessPermissions: getApplicationAccessPermission(state),
  actionsByResource: getActionsGroupedByResource(state),
});

export default withRouterQuery(connect(mapStateToProps)(ModelsTable));
